import { HelpCircleIcon, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { useGetAllClassesWithSearch } from '~entities/classes/api/get-all-classes-with-search';
import { useGetAllEventsWithSearch } from '~entities/events/api/get-all-events-with-search';
import { useCreateTransactionCode } from '~entities/payment/api/create-transaction-code';
import type { TransactionCode } from '~entities/payment/api/get-transaction-codes';
import { useUpdateTransactionCode } from '~entities/payment/api/update-transaction-code';

import { SessionSelector } from '~features/shared/session-selector';

import useDebounce from '~shared/hooks/useDebounce';
import { Button } from '~shared/shadcn/ui/button';
import { Checkbox } from '~shared/shadcn/ui/checkbox';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '~shared/shadcn/ui/sheet';
import { Switch } from '~shared/shadcn/ui/switch';
import { Textarea } from '~shared/shadcn/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '~shared/shadcn/ui/tooltip';

type SelectableEntity = {
  label: string;
  value: string;
};

export interface TransactionCodesFormProps {
  isOpen: boolean;
  onClose: () => void;
  transactionCode: TransactionCode | null;
}

export function TransactionCodesForm({
  isOpen,
  onClose,
  transactionCode,
}: TransactionCodesFormProps) {
  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [selectedEntities, setSelectedEntities] = useState<
    Array<SelectableEntity>
  >([]);
  const [entitySearch, setEntitySearch] = useState<string>('');

  const [selectedSession, setSelectedSession] = useState<
    | {
        label: string;
        value: string;
      }
    | undefined
    | null
  >(null);

  const [isAllClassSelected, setIsAllClassSelected] = useState(false);

  useEffect(() => {
    if (transactionCode) {
      setName(transactionCode.code);
      setDescription(transactionCode.description);
      if (transactionCode.sessionName && transactionCode.sessionId) {
        setSelectedSession({
          label: transactionCode.sessionName,
          value: transactionCode.sessionId,
        });
      }
      setSelectedEntities(
        transactionCode.entities.map((c) => ({
          label: c.entityName,
          value: c.entityId,
        })),
      );
    }
  }, [transactionCode]);

  const debouncedEntitySearch = useDebounce(entitySearch, 500);

  const { data: classesData, isFetching: isFetchingClasses } =
    useGetAllClassesWithSearch({
      sessionId: selectedSession?.value,
      name: debouncedEntitySearch,
      limit: 100,
    });

  const { data: eventsData, isFetching: isFetchingEvents } =
    useGetAllEventsWithSearch({
      sessionId: selectedSession?.value,
      name: debouncedEntitySearch,
      isStudio: true,
      limit: 100,
    });

  const isFetching = isFetchingClasses || isFetchingEvents;

  const classes = (classesData?.data.classes ?? []).map((classItem) => ({
    label: classItem.title,
    value: classItem._id,
  }));
  const events = (eventsData?.data.events ?? []).map((eventItem) => ({
    label: eventItem.title,
    value: eventItem._id,
  }));
  const byId = new Map<string, SelectableEntity>();
  for (const entity of [...classes, ...events]) {
    byId.set(entity.value, entity);
  }
  const availableEntities = [...byId.values()];

  const mutation = useCreateTransactionCode();

  const updateMutation = useUpdateTransactionCode();

  const handleSave = async () => {
    try {
      await mutation.mutateAsync({
        code: name,
        description: description.trim() ? description : '',
        ...(selectedSession?.value && {
          sessionId: selectedSession.value,
          sessionName: selectedSession.label,
          entities: isAllClassSelected
            ? []
            : selectedEntities.map((c) => ({
                entityId: c.value,
                entityName: c.label,
              })),
          isAllClassSelected,
        }),
      });
      toast.success('Transaction Code created successfully');
      onClose();
    } catch (error) {
      console.error(error);
      toast.error('Failed to create transaction code');
    }
  };

  const handleUpdate = async () => {
    try {
      await updateMutation.mutateAsync({
        transactionCodeId: transactionCode?._id ?? '',
        code: name,
        description: description.trim() ? description : '',
        ...(selectedSession?.value
          ? {
              sessionId: selectedSession.value,
              sessionName: selectedSession.label,
              entities: isAllClassSelected
                ? []
                : selectedEntities.map((c) => ({
                    entityId: c.value,
                    entityName: c.label,
                  })),
              isAllClassSelected,
            }
          : {
              sessionId: null,
              sessionName: null,
              entities: [],
              isAllClassSelected: false,
            }),
      });
      toast.success('Transaction Code updated successfully');
      onClose();
    } catch (error) {
      console.error(error);
      toast.error('Failed to update transaction code');
    }
  };

  const handleEntityToggle = (
    checked: boolean | 'indeterminate',
    entity: SelectableEntity,
  ) => {
    if (checked === true) {
      setSelectedEntities((current) => [...current, entity]);
    } else {
      setSelectedEntities((current) =>
        current.filter((c) => c.value !== entity.value),
      );
    }
  };

  const resetAndClose = () => {
    setName('');
    setDescription('');
    setSelectedEntities([]);
    setSelectedSession(null);
    setIsAllClassSelected(false);
    setEntitySearch('');
    onClose();
  };

  return (
    <Sheet open={isOpen} onOpenChange={resetAndClose}>
      <SheetContent className='flex h-full flex-col sm:max-w-md'>
        <SheetHeader>
          <SheetTitle>
            {transactionCode ? 'Edit Transaction Code' : 'New Transaction Code'}
          </SheetTitle>
        </SheetHeader>

        <div className='flex-1 overflow-y-auto px-1 py-4'>
          <div className='space-y-5'>
            <div className='space-y-2'>
              <Label htmlFor='name'>Transaction Code Name</Label>
              <Input
                id='name'
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                }}
                placeholder='Enter transaction code name'
              />
            </div>

            <div className='space-y-2'>
              <Label htmlFor='description'>Description</Label>
              <Textarea
                id='description'
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                }}
                placeholder='Enter a description for this transaction code'
                rows={2}
              />
            </div>

            <SessionSelector
              session={selectedSession}
              onChange={(val) => {
                setSelectedSession(val ?? null);
              }}
            />

            {selectedSession?.value && (
              <div className='space-y-3'>
                <div className='flex items-center justify-between'>
                  <Label>Classes/Events</Label>
                  <div className='flex items-center space-x-2'>
                    <Switch
                      id='select-all-classes'
                      checked={isAllClassSelected}
                      onCheckedChange={setIsAllClassSelected}
                    />
                    <Label htmlFor='select-all-classes'>
                      Select All Classes/Events
                    </Label>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild className='size-4'>
                          <HelpCircleIcon />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>
                            All classes and events will be selected. Toggle off
                            to choose specific classes or events.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>

                {!isAllClassSelected && (
                  <>
                    <div className='relative'>
                      <Search className='absolute left-2.5 top-2.5 size-4 text-muted-foreground' />
                      <Input
                        type='search'
                        placeholder='Search classes/events...'
                        className='mb-2 pl-8'
                        value={entitySearch}
                        onChange={(e) => {
                          setEntitySearch(e.target.value);
                        }}
                      />
                    </div>
                    <div className='max-h-[180px] overflow-y-auto rounded-md border p-1'>
                      {isFetching ? (
                        <div className='flex h-32 items-center justify-center'>
                          <div className='size-8 animate-spin rounded-full border-4 border-gray-200 border-t-primary' />
                        </div>
                      ) : availableEntities.length > 0 ||
                        selectedEntities.length > 0 ? (
                        <div className='space-y-2 p-2'>
                          {selectedEntities.map((entity) => (
                            <div
                              key={entity.value}
                              className='flex items-center space-x-2'
                            >
                              <Checkbox
                                id={`entity-${entity.value}`}
                                checked={selectedEntities.some(
                                  (c) => c.value === entity.value,
                                )}
                                onCheckedChange={(checked) => {
                                  handleEntityToggle(checked, entity);
                                }}
                              />
                              <label
                                htmlFor={`entity-${entity.value}`}
                                className='text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70'
                              >
                                {entity.label}
                              </label>
                            </div>
                          ))}
                          {availableEntities
                            .filter(
                              (entity) =>
                                !selectedEntities.some(
                                  (selected) =>
                                    selected.value === entity.value,
                                ),
                            )
                            .map((entity) => (
                              <div
                                key={entity.value}
                                className='flex items-center space-x-2'
                              >
                                <Checkbox
                                  id={`entity-${entity.value}`}
                                  checked={selectedEntities.some(
                                    (c) => c.value === entity.value,
                                  )}
                                  onCheckedChange={(checked) => {
                                    handleEntityToggle(checked, entity);
                                  }}
                                />
                                <label
                                  htmlFor={`entity-${entity.value}`}
                                  className='text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70'
                                >
                                  {entity.label}
                                </label>
                              </div>
                            ))}
                        </div>
                      ) : (
                        <div className='p-4 text-center text-sm text-muted-foreground'>
                          No classes or events available
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        <SheetFooter className='mt-2 border-t pt-4'>
          <div className='flex w-full justify-end gap-2'>
            <Button variant='outline' onClick={resetAndClose}>
              Cancel
            </Button>
            {transactionCode ? (
              <Button
                onClick={handleUpdate}
                disabled={
                  !name.trim() ||
                  Boolean(
                    selectedSession?.value &&
                      !isAllClassSelected &&
                      selectedEntities.length === 0,
                  )
                }
              >
                Update
              </Button>
            ) : (
              <Button
                onClick={handleSave}
                disabled={
                  !name.trim() ||
                  Boolean(
                    selectedSession?.value &&
                      !isAllClassSelected &&
                      selectedEntities.length === 0,
                  )
                }
              >
                Save
              </Button>
            )}
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
